import { useEffect, useRef, useState } from "react";
import { useVoiceRecorder } from "../../hooks/useVoiceRecorder";
import { showToast } from "../../../toast";
import { formatDuration } from "../../../utils";
import { WaveformBars } from "./WaveformBars";

const CANCEL_PX = 80;
const LOCK_PX = 60;
const ARM_MS = 150;

export interface VoiceRecorderBarProps {
  onSend: (file: File, durationSec: number, waveform: number[]) => void;
  onActiveChange?: (active: boolean) => void;
  // True when the composer has text typed and is showing its own Send
  // button instead - CSS-hides (and disables) the idle mic button rather
  // than unmounting VoiceRecorderBar, since a gesture could in theory still
  // be in flight (see the micHidden comment below).
  hideIdleMic?: boolean;
}

// Owns the mic button, the WhatsApp-style press/hold/slide gesture, the
// recorder itself, and the recording/locked/preview UI - a self-contained
// unit ChatThreadScreen mounts once beside its composer. onActiveChange
// tells the parent when to hide its own textarea/attach/send row (true from
// the moment real recording starts, false again once sent/cancelled) -
// nothing changes visually during the brief pre-recording "armed" window,
// so that transition doesn't need to be communicated.
export function VoiceRecorderBar({ onSend, onActiveChange, hideIdleMic }: VoiceRecorderBarProps) {
  const recorder = useVoiceRecorder();
  const [armed, setArmed] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewTime, setPreviewTime] = useState(0);

  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const armTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingReleaseRef = useRef<"none" | "send" | "cancel">("none");
  const autoSendRef = useRef(false);
  const previewAudioRef = useRef<HTMLAudioElement>(null);
  const previewUrlRef = useRef<string | null>(null);

  const phase = recorder.state.phase;

  // Auto-send path: a plain hold-then-release (no lock, no cancel) stops
  // the recorder and immediately sends, rather than entering preview -
  // matches real WhatsApp. The locked path's explicit Stop button leaves
  // autoSendRef false, so that stop lands in the preview UI instead.
  useEffect(() => {
    if (phase !== "stopped" || !autoSendRef.current) return;
    autoSendRef.current = false;
    const { finalBlob, finalDurationSec, finalWaveform, finalExt } = recorder.state;
    if (finalBlob && finalDurationSec !== null && finalWaveform) {
      const file = new File([finalBlob], `voice-${Date.now()}${finalExt ?? ".webm"}`, { type: finalBlob.type });
      onSend(file, finalDurationSec, finalWaveform);
    }
    recorder.reset();
    onActiveChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    if (phase === "error" && recorder.state.errorMessage) {
      showToast(recorder.state.errorMessage, "error");
      recorder.reset();
      onActiveChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Preview playback of the just-recorded (not yet sent) clip.
  useEffect(() => {
    if (phase !== "stopped" || !recorder.state.finalBlob) {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      return;
    }
    const url = URL.createObjectURL(recorder.state.finalBlob);
    previewUrlRef.current = url;
    setPreviewTime(0);
    setPreviewPlaying(false);
    return () => {
      URL.revokeObjectURL(url);
      previewUrlRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, recorder.state.finalBlob]);

  function beginArm(e: React.PointerEvent<HTMLButtonElement>): void {
    if (phase !== "idle" || armed) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    setArmed(true);
    armTimerRef.current = setTimeout(() => {
      armTimerRef.current = null;
      void beginRecording();
    }, ARM_MS);
  }

  async function beginRecording(): Promise<void> {
    const ok = await recorder.start();
    setArmed(false);
    if (!ok) return; // the "error" effect above shows a toast
    onActiveChange?.(true);
    if (pendingReleaseRef.current === "send") {
      pendingReleaseRef.current = "none";
      autoSendRef.current = true;
      recorder.stop();
    } else if (pendingReleaseRef.current === "cancel") {
      pendingReleaseRef.current = "none";
      recorder.discard();
      onActiveChange?.(false);
    }
  }

  function handlePointerMove(e: React.PointerEvent<HTMLButtonElement>): void {
    if (phase !== "recording" || !pointerStartRef.current) return;
    const dx = e.clientX - pointerStartRef.current.x;
    const dy = e.clientY - pointerStartRef.current.y;
    if (dy <= -LOCK_PX && Math.abs(dy) > Math.abs(dx)) {
      recorder.lock();
      setCancelling(false);
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {}
      return;
    }
    if (dx <= -CANCEL_PX) setCancelling(true);
    else if (cancelling) setCancelling(false);
  }

  function handlePointerUp(): void {
    if (armTimerRef.current) {
      clearTimeout(armTimerRef.current);
      armTimerRef.current = null;
      setArmed(false);
      return; // released before the hold threshold - accidental tap, nothing recorded
    }
    if (phase === "idle" && armed) {
      // beginRecording() is still awaiting getUserMedia - defer the release.
      pendingReleaseRef.current = cancelling ? "cancel" : "send";
      return;
    }
    if (phase === "recording") {
      if (cancelling) {
        recorder.discard();
        setCancelling(false);
        onActiveChange?.(false);
      } else {
        autoSendRef.current = true;
        recorder.stop();
      }
    }
    // "locked"/"stopped": pointerup on the mic button is a no-op by design -
    // explicit Stop/Cancel/Send buttons take over from here.
  }

  function handleLockedStop(): void {
    recorder.stop(); // autoSendRef stays false -> lands in preview, not an immediate send
  }

  function handleLockedCancel(): void {
    recorder.discard();
    onActiveChange?.(false);
  }

  function handlePreviewCancel(): void {
    recorder.reset();
    onActiveChange?.(false);
  }

  function handlePreviewSend(): void {
    const { finalBlob, finalDurationSec, finalWaveform, finalExt } = recorder.state;
    if (finalBlob && finalDurationSec !== null && finalWaveform) {
      const file = new File([finalBlob], `voice-${Date.now()}${finalExt ?? ".webm"}`, { type: finalBlob.type });
      onSend(file, finalDurationSec, finalWaveform);
    }
    recorder.reset();
    onActiveChange?.(false);
  }

  function togglePreviewPlay(): void {
    const audio = previewAudioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play()?.catch(() => {});
    else audio.pause();
  }

  // Hidden (not unmounted - see the comment above the return below) once
  // locked/stopped, where the explicit Cancel/Stop/Send buttons take over -
  // or when the composer has text and is showing Send instead. Gated on
  // `phase === "idle" && !armed` so a press already in flight is never
  // yanked out from under the user's finger just because text changed.
  const micHidden = phase === "locked" || phase === "stopped" || (hideIdleMic && phase === "idle" && !armed);
  const micButton = (
    <button
      type="button"
      className={`chat-round-btn chat-voice-mic-btn flex-shrink-0${armed || phase === "recording" ? " is-active" : ""}${micHidden ? " is-hidden" : ""}`}
      aria-label="Hold to record a voice message"
      title="Hold to record"
      onPointerDown={beginArm}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      // Belt-and-suspenders alongside touch-action:none in CSS - on some
      // browsers a sustained press still fires its own contextmenu (e.g.
      // Android's "copy/share" callout) independently of touch-action,
      // which would otherwise interrupt the hold.
      onContextMenu={(e) => e.preventDefault()}
      disabled={micHidden}
    >
      <i className="bi bi-mic-fill" aria-hidden="true" />
    </button>
  );

  // Single stable root, mic button always the first child at the same tree
  // position across every phase (only CSS-hidden once locked/stopped, never
  // unmounted) - setPointerCapture ties the in-flight gesture to that exact
  // DOM node, so swapping its parent/position mid-gesture (e.g. returning a
  // bare <button> in one phase and a <div>-wrapped one in another) would
  // make React remount it and silently break drag tracking.
  const expanded = phase !== "idle" && phase !== "error" && !armed;
  const duration = recorder.state.finalDurationSec ?? 0;

  return (
    <div className={`chat-voice-recorder-bar${expanded ? " is-expanded" : ""}`}>
      {micButton}

      {phase === "recording" && (
        <>
          <span className="chat-voice-rec-dot" aria-hidden="true" />
          <span className="chat-voice-rec-time">{formatDuration(recorder.state.elapsedMs / 1000)}</span>
          <WaveformBars levels={recorder.state.liveLevels} className="chat-waveform-live" />
          <span className={`chat-voice-hint${cancelling ? " is-cancelling" : ""}`}>
            {cancelling ? "Release to cancel" : "< Slide to cancel  •  Slide up to lock"}
          </span>
        </>
      )}

      {phase === "locked" && (
        <>
          <button type="button" className="chat-round-btn flex-shrink-0" aria-label="Cancel recording" onClick={handleLockedCancel}>
            <i className="bi bi-trash" aria-hidden="true" />
          </button>
          <span className="chat-voice-rec-dot" aria-hidden="true" />
          <span className="chat-voice-rec-time">{formatDuration(recorder.state.elapsedMs / 1000)}</span>
          <WaveformBars levels={recorder.state.liveLevels} className="chat-waveform-live" />
          <button type="button" className="chat-round-btn chat-round-btn-filled flex-shrink-0" aria-label="Stop recording" onClick={handleLockedStop}>
            <i className="bi bi-stop-fill" aria-hidden="true" />
          </button>
        </>
      )}

      {phase === "stopped" && (
        <>
          {previewUrlRef.current && (
            <audio
              ref={previewAudioRef}
              src={previewUrlRef.current}
              hidden
              onTimeUpdate={(e) => setPreviewTime(e.currentTarget.currentTime)}
              onPlay={() => setPreviewPlaying(true)}
              onPause={() => setPreviewPlaying(false)}
              onEnded={() => {
                setPreviewPlaying(false);
                setPreviewTime(0);
              }}
            />
          )}
          <button type="button" className="chat-round-btn flex-shrink-0" aria-label="Discard recording" onClick={handlePreviewCancel}>
            <i className="bi bi-trash" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="chat-round-btn flex-shrink-0"
            aria-label={previewPlaying ? "Pause preview" : "Play preview"}
            onClick={togglePreviewPlay}
          >
            <i className={`bi ${previewPlaying ? "bi-pause-fill" : "bi-play-fill"}`} aria-hidden="true" />
          </button>
          <WaveformBars
            levels={recorder.state.finalWaveform ?? []}
            progress={duration ? previewTime / duration : 0}
            onSeek={(f) => {
              if (previewAudioRef.current) previewAudioRef.current.currentTime = f * duration;
            }}
            className="chat-waveform-playback"
          />
          <span className="chat-voice-rec-time">{formatDuration(previewPlaying || previewTime > 0 ? previewTime : duration)}</span>
          <button type="button" className="chat-round-btn chat-round-btn-filled flex-shrink-0" aria-label="Send voice message" onClick={handlePreviewSend}>
            <i className="bi bi-send-fill" aria-hidden="true" />
          </button>
        </>
      )}
    </div>
  );
}
